import React from 'react';
import PrintLayout from './PrintLayout';
import { ALL_CODES } from '../../utils/shortcodes';

function msToMMSS(ms) {
  if (!ms) return '0:00';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

function buildSpeakerList(entries) {
  const map = {};
  (entries || []).forEach(e => {
    if (!e.speakerName) return;
    const name = e.speakerDisplayName || e.speakerName.split('@')[0];
    const ms = (e.endTime && e.startTime) ? e.endTime - e.startTime : 0;
    map[name] = (map[name] || 0) + ms;
  });
  return Object.entries(map).sort((a, b) => b[1] - a[1]);
}

export default function PrintTopics({ pageNumber, reportData, meeting }) {
  const topics = reportData.topics || [];
  const feedback = meeting.topicFeedback || {};

  const TH = {
    padding: '7px 10px', textAlign: 'left', fontWeight: 700,
    letterSpacing: '0.5px', color: 'white', fontSize: 9,
    fontFamily: 'Montserrat, sans-serif',
  };

  const ratingColor = (n) => {
    if (n === 5) return '#cba43a';
    if (n >= 3) return '#5f7942';
    return '#922b3f';
  };

  const RatingBoxes = ({ value }) => {
    if (!value) return <span style={{ color: '#aaa', fontSize: 9 }}>—</span>;
    if (value === 'na') return <span style={{ fontSize: 9 }}>N/A</span>;
    const n = parseInt(value, 10);
    const color = ratingColor(n);
    return (
      <div style={{ display: 'flex', gap: 2 }}>
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} style={{
            width: 10, height: 10, borderRadius: 2,
            background: i <= n ? color : 'transparent',
            border: `1.5px solid ${color}`,
          }} />
        ))}
      </div>
    );
  };

  return (
    <PrintLayout pageNumber={pageNumber}>
      <div style={{ position: 'absolute', top: 0, bottom: 68, left: 0, right: 0, padding: '5% 6%', background: '#f5f4f0', overflow: 'hidden' }}>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 24, fontWeight: 700, color: '#1e2e45', marginBottom: 16 }}>
          Meeting Topics Breakdown
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9.5 }}>
          <thead>
            <tr style={{ background: '#1e2e45' }}>
              <th style={{ ...TH, width: '40%' }}>Topic</th>
              <th style={TH}>Time</th>
              <th style={{ ...TH, textAlign: 'center' }}>Spkrs</th>
              <th style={{ ...TH, textAlign: 'center' }}>Qs</th>
              <th style={TH}>Level of Decision</th>
              <th style={TH}>Level of Debate</th>
            </tr>
          </thead>
          <tbody>
            {topics.map((t, i) => {
              const fb = feedback[t.label] || {};
              const speakerList = buildSpeakerList(t.entries);
              const geniusList = Object.entries(t.genius || {})
                .map(([code, count]) => ({ code, count, def: ALL_CODES[code] }))
                .filter(g => g.def && g.count > 0);
              const rowBg = i % 2 === 1 ? 'white' : undefined;

              return (
                <tr key={t.label} style={{ background: rowBg, verticalAlign: 'top' }}>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid rgba(42,61,91,0.12)', color: '#2a3d5b' }}>
                    <div style={{ fontWeight: 700, fontSize: 9.5, marginBottom: speakerList.length || geniusList.length ? 5 : 0 }}>
                      {t.label}
                    </div>
                    {speakerList.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: geniusList.length ? 4 : 0 }}>
                        {speakerList.map(([name, ms]) => (
                          <span key={name} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            padding: '1px 7px', background: 'rgba(42,61,91,0.07)',
                            border: '1px solid rgba(42,61,91,0.15)', borderRadius: 8,
                            fontSize: 8, color: '#2a3d91',
                          }}>
                            {name}{ms > 0 && <span style={{ color: '#8899aa' }}> · {msToMMSS(ms)}</span>}
                          </span>
                        ))}
                      </div>
                    )}
                    {geniusList.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {geniusList.map(({ code, count, def }) => (
                          <span key={code} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 2,
                            padding: '1px 6px',
                            background: def.color + '18',
                            border: `1px solid ${def.color}44`,
                            borderRadius: 8, fontSize: 7.5, fontWeight: 700,
                            color: def.color,
                          }}>
                            {def.emoji} {def.label}{count > 1 ? ` ×${count}` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid rgba(42,61,91,0.12)', color: '#2a3d5b', fontSize: 9.5, whiteSpace: 'nowrap' }}>
                    {msToMMSS(t.ms)}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid rgba(42,61,91,0.12)', textAlign: 'center', color: '#2a3d5b', fontSize: 9.5, fontWeight: 600 }}>
                    {t.speakers ?? speakerList.length}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid rgba(42,61,91,0.12)', textAlign: 'center', color: '#2a3d5b', fontSize: 9.5, fontWeight: 600 }}>
                    {t.questions ?? 0}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid rgba(42,61,91,0.12)' }}>
                    <RatingBoxes value={fb.decision} />
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid rgba(42,61,91,0.12)' }}>
                    <RatingBoxes value={fb.debate} />
                  </td>
                </tr>
              );
            })}
            {topics.length === 0 && (
              <tr>
                <td style={{ padding: '8px 10px', fontStyle: 'italic', color: '#6b757e', fontSize: 9.5 }} colSpan={6}>
                  No topics recorded
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </PrintLayout>
  );
}
